// frontend/src/logic/useBranding.js
import { ref, computed, watch } from 'vue';
import { config } from '@/configState'; // Zugriff auf die zentrale Config (Env + DB)

// MODULE-LEVEL SINGLETON: themeData wird EINMAL erstellt und von ALLEN Komponenten geteilt
// Das ist der einzige Weg, um Reaktivität über Component-Grenzen hinweg zu garantieren
export const themeData = ref({
    colors: { primary: "#D500F9", secondary: "#212121", accent: "#00BCD4", error: "#FF5252", background: "#0D1117", surface: "#161B22" },
    shape: { globalRadius: "24px" },
    caller: {}
});

// Hex zu RGB für Vuetify CSS Variablen (Vuetify erwartet RGB-Werte)
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
}

// Diese Funktion wendet die CSS-Variablen an den :root (html) Tag an
const applyBrandingCSS = (t) => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;

    // DEBUG: Logge alle gesetzten Variablen
    console.log('🎨 [applyBrandingCSS] Setze CSS-Variablen:', t);

    // 1. Shapes
    if (t.shape?.globalRadius) {
        root.style.setProperty('--custom-border-radius', t.shape.globalRadius);
        console.log('   --custom-border-radius:', t.shape.globalRadius);
    }

    // 2. Caller Dashboard Specifics
    if (t.caller) {
        const c = t.caller;
        const set = (name, val) => {
            if (val) {
                root.style.setProperty(name, val);
                console.log(`   ${name}:`, val);
            }
        };

        set('--overlay-bg', c.overlayBg);
        if (c.overlayBgImage) root.style.setProperty('--overlay-bg-image', `url('${c.overlayBgImage}')`);

        set('--video-border', c.videoBorder);
        set('--video-border-radius', c.videoBorderRadius);
        set('--audio-bg-image', c.audioBgImage);
        set('--card-bg-opacity', c.cardBgOpacity);

        // Clock Styles
        set('--clock-bg', c.clockBg);
        set('--clock-border', c.clockBorder);
        set('--clock-text-glow', c.clockTextGlow);
        set('--clock-color-ok', c.clockColorOk);
        set('--clock-color-warn', c.clockColorWarn);
        set('--clock-color-panic', c.clockColorPanic);

        // Overlays / Gradients
        if (c.footerGradient) root.style.setProperty('--footer-gradient', c.footerGradient);
        if (c.photoOverlayGradient) root.style.setProperty('--photo-overlay-gradient', c.photoOverlayGradient);

        // Hover effects (operator cards, balance-section)
        if (c.hoverBg) root.style.setProperty('--hover-bg', c.hoverBg);
        if (c.hoverBorder) root.style.setProperty('--hover-border', c.hoverBorder);
    }

    // 3. Colors – eigene CSS-Vars + Vuetify --v-theme-* + direkte Utility-Klassen
    if (t.colors) {
        // Sammle alle RGB-Werte für den Style-Tag
        let vuetifyVars = '';
        let utilityStyles = '';

        Object.entries(t.colors).forEach(([key, val]) => {
            // Eigene CSS-Variable
            root.style.setProperty(`--color-${key}`, val);
            // Vuetify 3 CSS-Variable (direkt auf dem Element)
            const rgb = hexToRgb(val);
            if (rgb) {
                root.style.setProperty(`--v-theme-${key}`, rgb);
                vuetifyVars += `  --v-theme-${key}: ${rgb};\n`;
            }
        });

        // Style-Tag mit höchster Priorität injizieren – targetiert alle Vuetify-Selektoren
        let styleEl = document.getElementById('dynamic-vuetify-theme');
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = 'dynamic-vuetify-theme';
            document.head.appendChild(styleEl);
        }
        styleEl.textContent = `:root, .v-application, .v-theme--dark, .v-theme--light {\n${vuetifyVars}}`;
    }

    // 4. Typography – Font asynchron laden (blockiert nicht!)
    if (t.typography?.fontFamily) {
        // Font via <link> laden (Google Fonts o.ä.)
        if (t.typography.fontUrl) {
            let linkEl = document.getElementById('dynamic-font-link');
            if (!linkEl) {
                linkEl = document.createElement('link');
                linkEl.id = 'dynamic-font-link';
                linkEl.rel = 'stylesheet';
                // Preload für schnelleres Laden
                linkEl.as = 'style';
                linkEl.onload = () => {
                    linkEl.rel = 'stylesheet';
                };
                document.head.appendChild(linkEl);
            }
            linkEl.href = t.typography.fontUrl;
        }
        // Font-Family auf der Wurzel setzen
        root.style.setProperty('--font-family', t.typography.fontFamily);
        root.style.fontFamily = t.typography.fontFamily;
    }

    // DEBUG: Prüfe ob Variablen tatsächlich gesetzt wurden
    console.log('✅ CSS-Variablen gesetzt. Prüfwerte:');
    console.log('   --color-primary:', getComputedStyle(root).getPropertyValue('--color-primary'));
    console.log('   --color-surface:', getComputedStyle(root).getPropertyValue('--color-surface'));
};

/**
 * Lädt die spezifische JSON Datei aus public/themes/
 * z.B. /themes/velvet.json oder /themes/royal.json
 */
const loadThemeFile = async (filename) => {
    if (!filename) return;

    try {
        console.log(`🎨 Lade Theme: ${filename}`);
        // Cache-Busting, damit Änderungen sofort sichtbar sind
        const res = await fetch(`/themes/${filename}.json?t=${Date.now()}`);

        if (res.ok) {
            const json = await res.json();

            // Wir nutzen nur den "theme" Block aus der JSON.
            // Der "meta" Block in der JSON wird ignoriert (Name/Logo kommen jetzt aus der .env/Config)
            if (json.theme) {
                // Deep merge um caller properties zu erhalten
                themeData.value = {
                    colors: { ...themeData.value.colors, ...json.theme.colors },
                    shape: { ...themeData.value.shape, ...json.theme.shape },
                    caller: json.theme.caller || themeData.value.caller
                };
                applyBrandingCSS(json.theme);
                console.log(`✅ Theme ${filename} erfolgreich geladen`, themeData.value);
            }
        } else {
            console.warn(`Theme File '${filename}' nicht gefunden.`);
        }
    } catch (e) {
        console.warn("Branding load failed, using defaults.", e);
    }
};

/**
 * Aktualisiert Titel und Favicon im Browser-Tab
 * (Basierend auf der Config aus der .env, NICHT aus der JSON)
 */
const updateMetaTags = () => {
    if (typeof document === 'undefined') return;

    // Titel setzen
    if (config.meta.appTitle) {
        document.title = config.meta.appTitle;
    }

    // Favicon setzen
    if (config.meta.favicon) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = config.meta.favicon;
    }
};


// Globale Variable für Vuetify instance (wird von App.vue gesetzt)
let vuetifyInstance = null;

export function setVuetifyInstance(vuetify) {
    vuetifyInstance = vuetify;
}

// Funktion zum Aktualisieren von Vuetify's Theme (global aufrufbar)
export function updateVuetifyTheme(colors) {
    if (!colors || typeof window === 'undefined') return;

    const themeColors = {
        primary: colors.primary || '#D500F9',
        secondary: colors.secondary || '#212121',
        accent: colors.accent || '#00BCD4',
        error: colors.error || '#FF5252',
        background: colors.background || '#0D1117',
        surface: colors.surface || '#161B22',
        success: colors.primary || '#D500F9',
        warning: colors.accent || '#00BCD4',
        info: colors.accent || '#00BCD4',
    };

    const root = document.documentElement;
    Object.entries(themeColors).forEach(([key, val]) => {
        const rgb = hexToRgb(val);
        if (rgb) {
            root.style.setProperty(`--v-theme-${key}`, rgb);
        }
    });

    // Auch über die Vuetify API versuchen (falls verfügbar)
    if (vuetifyInstance) {
        try {
            const currentThemeName = vuetifyInstance.theme.global.name.value;
            const themeDefinition = vuetifyInstance.theme.themes.value[currentThemeName];
            if (themeDefinition) {
                Object.assign(themeDefinition.colors, themeColors);
                // Temporären Namen setzen, dann zurück – erzwingt Re-Rendering
                vuetifyInstance.theme.global.name.value = 'temp-repaint-trigger';
                setTimeout(() => {
                    vuetifyInstance.theme.global.name.value = currentThemeName;
                }, 50);
            }
        } catch(e) {
            console.warn('[updateVuetifyTheme] Vuetify API error:', e);
        }
    }

    console.log('✅ Vuetify Theme aktualisiert:', themeColors);
}

export function useBranding() {
    // 1. WATCHER: Sobald die Config geladen ist (App Start), Meta-Tags setzen
    watch(() => config.meta, () => {
        updateMetaTags();
    }, { deep: true, immediate: true });

    // 2. WATCHER: Sobald sich das aktive Theme ändert (Start oder Admin-Switch), JSON laden
    watch(() => config.activeTheme, (newThemeName, oldThemeName) => {
        if (newThemeName && newThemeName !== oldThemeName) {
            console.log(`🎨 [useBranding] Theme: ${oldThemeName ? `gewechselt von ${oldThemeName} nach ${newThemeName}` : `initial geladen: ${newThemeName}`}`);
            loadThemeFile(newThemeName);
        }
    }, { immediate: true });

    // 3. WATCHER: Wenn sich themeData ändert, Vuetify Theme auch updaten
    watch(() => themeData.value.colors, (newColors) => {
        updateVuetifyTheme(newColors);
    }, { deep: true, immediate: true });

    return {
        // App Name kommt jetzt aus der Config (Env/API)
        appName: computed(() => config.meta.appName || 'Loading...'),

        // Logo URL (für Bilder im Template)
        logoUrl: computed(() => config.meta.logoUrl),

        // Styling Daten (Farben etc.)
        branding: computed(() => ({ theme: themeData.value })),

        // Funktion für den Admin, um das Theme live zu wechseln
        switchTheme: (newThemeName) => {
            config.activeTheme = newThemeName;
            // Der Watcher oben übernimmt dann automatisch das Laden
        }
    };
}